import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import useModal from "components/modal/useModal";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import ModalFooter from "components/modal/components/footer";
import Button from "components/button";
import LoadingButton from "components/button/loading";
import StyledInput from "components/styledInput/StyledInput";
import InfoBox from "components/infoBox/InfoBox";
import PrivatePoolInviteLink from "components/privatePool/PrivatePoolInviteLink";
import {
    NAME_MAX_LENGTH,
    isValidPoolContact,
    isValidPoolName,
} from "components/privatePool/privatePoolContact";
import { formatFullName } from "helper/personName";

const ERROR_CODE = {
    ALREADY_IN_POOL: "ALREADY_IN_POOL",
    ALREADY_PLACED_IN_GROUP: "ALREADY_PLACED_IN_GROUP",
    INVALID_INPUT: "INVALID_INPUT",
};

const LINK_STATUS = { LOADING: "loading", READY: "ready", ERROR: "error" };

/**
 * "Ajouter au vivier privé" (cM_add_private_pool), opened from the private pool tab of the
 * invitation step, which passes its callbacks through the context's `privatePoolModalData`.
 * A temp worker Jobbiz already placed in the group cannot join the pool: the modal then offers
 * to invite them on the mission as a Jobbiz profile instead.
 */
const AddPrivatePoolModal = () => {
    const { language, companyData, privatePoolModalData, setPrivatePoolModalData } = UseContext();
    const { toggle } = useModal();
    const label = (key, defaultValue) => language.getValue(`privatePool.addModal.${key}`, defaultValue);
    const idCompany = companyData?._id;
    const companyName = companyData?.name_commercial || companyData?.reason_social || "";

    const [firstname, setFirstname] = useState("");
    const [lastname, setLastname] = useState("");
    const [contact, setContact] = useState("");
    const [contactError, setContactError] = useState("");
    // The temp worker of a 409 ALREADY_PLACED_IN_GROUP: { _id, firstname, lastname }.
    const [placedTempWorker, setPlacedTempWorker] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [inviteLink, setInviteLink] = useState({ status: LINK_STATUS.LOADING, url: "" });

    useEffect(() => {
        if (!idCompany) {
            setInviteLink({ status: LINK_STATUS.ERROR, url: "" });
            return undefined;
        }
        let isCurrent = true;
        ApiDatabase.getPrivatePoolInviteLink(
            { idCompany },
            (response) => {
                if (!isCurrent) return;
                const url = response?.data?.url;
                setInviteLink(url ? { status: LINK_STATUS.READY, url } : { status: LINK_STATUS.ERROR, url: "" });
            },
            (err) => {
                console.error(err);
                if (isCurrent) setInviteLink({ status: LINK_STATUS.ERROR, url: "" });
            }
        );
        return () => {
            isCurrent = false;
        };
    }, [idCompany]);

    const canSubmit = isValidPoolName(firstname) && isValidPoolName(lastname) && isValidPoolContact(contact);

    const close = () => {
        if (isSaving) return;
        setPrivatePoolModalData(null);
        toggle();
    };

    // Any edit leaves the "already placed" state and clears the field error.
    const updateField = (setter) => (event) => {
        setter(event.target.value);
        setContactError("");
        setPlacedTempWorker(null);
    };

    const handleError = (err) => {
        setIsSaving(false);
        const responseData = err?.response?.data;
        const errorCode = responseData?.errorCode;
        if (errorCode === ERROR_CODE.ALREADY_IN_POOL) {
            setContactError(label("alreadyInPool", "Ce candidat est déjà dans votre vivier."));
            return;
        }
        // The api checks the fields more strictly than the form (e.g. a phone number it cannot read).
        if (err?.response?.status === 400 && errorCode === ERROR_CODE.INVALID_INPUT) {
            setContactError(label("invalidInput", "Vérifiez le prénom, le nom et le téléphone ou l'e-mail."));
            return;
        }
        if (errorCode === ERROR_CODE.ALREADY_PLACED_IN_GROUP && responseData?.tempWorker?._id) {
            setPlacedTempWorker(responseData.tempWorker);
            return;
        }
        console.error(err);
        toast.error(label("error", "Le candidat n'a pas pu être ajouté, réessayez plus tard."));
    };

    const submit = () => {
        if (!canSubmit || isSaving || !idCompany) return;
        setIsSaving(true);
        ApiDatabase.postPrivatePoolMember(
            { idCompany, firstname: firstname.trim(), lastname: lastname.trim(), contact: contact.trim() },
            (response) => {
                setIsSaving(false);
                privatePoolModalData?.onMemberAdded?.(response?.data);
                setPrivatePoolModalData(null);
                toggle();
            },
            handleError
        );
    };

    const inviteTempWorker = () => {
        privatePoolModalData?.onTempWorkerInvited?.(placedTempWorker);
        setPrivatePoolModalData(null);
        toggle();
    };

    const submitOnEnter = (event) => {
        if (event.key === "Enter") (placedTempWorker ? inviteTempWorker : submit)();
    };

    const placedName =
        formatFullName(placedTempWorker?.firstname, placedTempWorker?.lastname) || formatFullName(firstname, lastname);
    const showInviteLink = !placedTempWorker && inviteLink.status !== LINK_STATUS.ERROR;

    const renderConfirmButton = () => {
        if (placedTempWorker) {
            return (
                <Button size="SM" color="PRIMARY" onClick={inviteTempWorker}>
                    {label("inviteOnMission", "L'inviter sur cette mission")}
                </Button>
            );
        }
        if (isSaving) {
            return (
                <LoadingButton color="PRIMARY" size="SM">
                    {label("submit", "Ajouter")}
                </LoadingButton>
            );
        }
        return (
            <Button
                size="SM"
                color={canSubmit ? "PRIMARY" : "DISABLED"}
                disabled={!canSubmit}
                onClick={submit}
            >
                {label("submit", "Ajouter")}
            </Button>
        );
    };

    return (
        <>
            <Modalheader
                hide={close}
                hideShow={true}
                closeLabel={label("close", "Fermer")}
                description={label(
                    "description",
                    "Il pourra être invité sur vos missions. Jobbiz l'embauche en intérim pour vous, à un tarif réduit."
                )}
            >
                {label("title", "Ajouter au vivier privé")}
            </Modalheader>
            <ModalBody>
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-4">
                        <StyledInput
                            type={"text"}
                            name="firstname"
                            label={label("firstname", "Prénom")}
                            value={firstname}
                            maxLength={NAME_MAX_LENGTH}
                            onChange={updateField(setFirstname)}
                            onKeyDown={submitOnEnter}
                        />
                        <StyledInput
                            type={"text"}
                            name="lastname"
                            label={label("lastname", "Nom")}
                            value={lastname}
                            maxLength={NAME_MAX_LENGTH}
                            onChange={updateField(setLastname)}
                            onKeyDown={submitOnEnter}
                        />
                    </div>
                    <StyledInput
                        type={"text"}
                        name="contact"
                        label={label("contact", "Téléphone ou e-mail")}
                        value={contact}
                        onChange={updateField(setContact)}
                        onKeyDown={submitOnEnter}
                        error={contactError}
                        hint={
                            placedTempWorker
                                ? undefined
                                : label("contactHint", "Nous lui envoyons un SMS ou un e-mail pour rejoindre votre vivier.")
                        }
                    />
                    {placedTempWorker ? (
                        <InfoBox
                            title={label("placedInGroupTitle", "{name} a déjà travaillé avec votre groupe").replace(
                                "{name}",
                                placedName
                            )}
                        >
                            {label(
                                "placedInGroupDescription",
                                "Jobbiz l'a déjà placé dans votre groupe, il ne peut donc pas rejoindre le vivier privé. Vous pouvez l'inviter sur cette mission."
                            )}
                        </InfoBox>
                    ) : null}
                    {showInviteLink ? (
                        <>
                            <div className="flex items-center gap-3" aria-hidden="true">
                                <div className="h-px flex-1 bg-gray-200" />
                                <span className="ft-xs text-gray-400">{label("or", "ou")}</span>
                                <div className="h-px flex-1 bg-gray-200" />
                            </div>
                            <PrivatePoolInviteLink url={inviteLink.url} companyName={companyName} />
                        </>
                    ) : null}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button size="SM" color="SECONDARY" onClick={close}>
                    {label("cancel", "Annuler")}
                </Button>
                {renderConfirmButton()}
            </ModalFooter>
        </>
    );
};

export default AddPrivatePoolModal;
